import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { getApiErrorMessage } from '@/api/apiErrors';
import { InputForm } from '@/components/CustomForm/components';
import { Button } from '@/components/common/Button';
import { LoginLayout } from '@/components/layout/LoginLayout';
import { useAuth } from '@/providers/useAuth';
import { EmailAddressIcon, EyeIcon, PasswordIcon } from '@/assets/icons/onboarding/icons';

type LoginValues = {
  email: string;
  password: string;
};

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoggingIn } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<LoginValues>({
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onChange',
  });

  const redirectPath = typeof location.state === 'object' && location.state && 'from' in location.state
    ? String(location.state.from)
    : '/';

  const onSubmit = handleSubmit(async (values) => {
    try {
      await login(values);
      navigate(redirectPath, { replace: true });
    } catch (error) {
      toast.error(getApiErrorMessage(error, t('auth.invalidCredentials')));
    }
  });

  return (
    <LoginLayout title="">
      <form className="stack auth-form" onSubmit={onSubmit}>
        <InputForm
          className="login-email field--onboarding"
          type="email"
          placeholder="Email Address"
          autoComplete="email"
          variant="onboarding"
          leftIcon={<EmailAddressIcon className="onboarding-icon" />}
          error={errors.email?.message}
          {...register('email', {
            required: 'Email is required.',
          })}
        />
        <InputForm
          className="login-password field--onboarding"
          type="password"
          placeholder="Enter your password"
          autoComplete="current-password"
          variant="onboarding"
          leftIcon={<PasswordIcon className="onboarding-icon" />}
          rightIcon={<EyeIcon className="onboarding-icon" />}
          error={errors.password?.message}
          {...register('password', {
            required: 'Password is required.',
          })}
        />
        <Button 
          className="login-submit button--block" 
          variant="onboarding" 
          type="submit" 
          disabled={isLoggingIn || !isValid}
        >
          {isLoggingIn ? t('auth.signingIn') : t('auth.submit')}
        </Button>
        <div className="auth-form__links">
          <Link 
            className="table-link" 
            to="/forgot-password"
          >
            {t('auth.forgotPassword')}
          </Link>
        </div>
      </form>
    </LoginLayout>
  );
}
